import api from './api';

export interface FacturaCompra {
    nroFormulario: number;
    fechaEmision: string | Date;
    fechaVencim?: string | Date;
    aplicacionIva?: string | Date;
    horaEmision?: string | Date;
    nroTipoFormulario?: string;
    tipoCliente?: string;
    tipoProveedor?: string;
    letra?: string;
    tipoIva?: string;
    nroSuc?: number;
    nroComprobante?: number;
    nroProveedor?: number;
    nombre?: string;
    nroLista?: string;
    codigoMoneda?: number | string;
    subtotal?: number;
    descuento?: number;
    importeSinIva?: number;
    exento?: number;
    ivainscripto?: number;
    ivanoInscripto?: number;
    ivaotro?: number;
    importeTotal?: number;
    asentado?: string;
    descripcion?: string;
    usuario?: string;
    cuit?: string;
    nroUbicacionDestino?: number;
    ubicacionDestinoDescripcion?: string;
    formaDePago?: string;
    formaEntrega?: string;
    ordenDeCompra?: string;
    porcDesc?: number;
    porcDesc2?: number;
    porcDesc3?: number;
    percepciones?: number;
    impuestos?: ImpuestoPorFormularioReadDto[];
    detalles?: FacturaCompraDetalle[];
}

export interface ImpuestoPorFormularioReadDto {
    codigoImpuesto: string;
    montoBaseImp?: number;
    montoImpuesto?: number;
    ctaCtable?: number;
}

export interface FacturaCompraDetalle {
    nroIdformulario: number;
    nroItem: number;
    codigoArticulo?: string;
    descripcionArticulo?: string;
    cantidad?: number;
    precioLista?: number;
    porcDesc?: number;
    precioUnitario?: number;
    descuento?: number;
    subtotal?: number;
    notas?: string;
    tipoProveedor?: string;
    nroCuenta?: number;
}

export interface FacturaCompraSearchParams {
    searchTerm?: string;
    pageNumber: number;
    pageSize: number;
    fechaDesde?: Date | string;
    fechaHasta?: Date | string;
    nroProveedor?: string;
    asentado?: string;
    usuario?: string;
    letra?: string;
    nroSuc?: number;
    nroComprobante?: number;
    nombreProveedor?: string;
}

export interface FacturaCompraPagedResult {
    items: FacturaCompra[];
    totalCount: number;
    pageNumber: number;
    pageSize: number;
    totalPages: number;
}

/** Item del Libro IVA Compras (respuesta del endpoint ComprobantesCompra/libro-iva-compras) */
export interface LibroIvaComprasItem {
    nroformulario: number;
    fechaemision?: string;
    nroTipoFormulario?: string;
    documento?: string;
    letraSucNum?: string;
    nombre?: string;
    tipoIva?: string;
    cuit?: string;
    netoGrav?: number;
    excento?: number;
    iva10?: number;
    iva21?: number;
    iva27?: number;
    retenc?: number;
    aplicacionIva?: string;
    perIibb?: number;
    retIva?: number;
    importeTotal?: number;
    letra?: string;
    nroSuc?: number;
    nroComprobante?: number;
}

/** Resultado paginado del Libro IVA Compras */
export interface LibroIvaComprasPagedResult {
    items: LibroIvaComprasItem[];
    totalCount: number;
    pageNumber: number;
    pageSize: number;
    totalPages: number;
    totalNetoGrav?: number;
    totalExcento?: number;
    totalIva10?: number;
    totalIva21?: number;
    totalIva27?: number;
    totalImporteTotal?: number;
}

/** Parámetros de búsqueda para libro IVA compras (endpoint distinto) */
export interface LibroIvaFacturaCompraSearchParams {
    searchTerm?: string;
    pageNumber: number;
    pageSize: number;
    fechaDesde?: Date;
    fechaHasta?: Date;
    nroProveedor?: number;
    nroSuc?: number;
    nombreProveedor?: string;
}

export interface FacturaCompraFormularioDto {
    idCompania: string;
    letra: string;
    nroSuc: number;
    nroComprobante: number;
    tipoProveedor: string;
    tipoComprobante: string;
    descripcion?: string;
    fechaEmision?: string;
    fechaVencim?: string;
    aplicacionIva?: string;
    nroProveedor: number;
    formaDePago?: string;
    nroUbicacionDestino?: number;
    porcDesc?: number;
    porcDesc2?: number;
    porcDesc3?: number;
}

export interface CreateItemCompraDto {
    codigoArticulo: string;
    descripcion?: string;
    cantidad: number;
    costoBrutoUnitario: number;
    porcDesc1?: number;
    porcDesc2?: number;
    porcDesc3?: number;
    porcDesc4?: number;
    porcDesc5?: number;
    um?: string;
    codigoMoneda?: number;
    cotizacion?: number;
    notas?: string;
    tasa1?: number;
    tasa2?: number;
    tasa3?: number;
    tasa4?: number;
    tasa5?: number;
    impInterno1?: number;
    impInterno2?: number;
    tasaHidrica?: number;
    /** Tipo de proveedor del item: C (productos) o G (conceptos) */
    tipoProveedor?: 'C' | 'G';
    /** Número de cuenta del imputable. Obligatorio para items tipo G */
    nroCuenta?: number;
}

/** Impuesto adicional del comprobante (percepciones, retenciones) */
export interface ImpuestoPorFormularioCreateDto {
    codigoImpuesto: string;
    ctaCtable: number;
    montoBaseImp?: number;
    montoImpuesto?: number;
}

export interface FacturaCompraCreatePayload {
    formulario: FacturaCompraFormularioDto;
    items: CreateItemCompraDto[];
    impuestos?: ImpuestoPorFormularioCreateDto[];
}

const facturaCompraService = {
    getFacturasCompra: async (params: FacturaCompraSearchParams): Promise<FacturaCompraPagedResult> => {
        const queryParams: Record<string, string | number | undefined> = {
            pageNumber: params.pageNumber,
            pageSize: params.pageSize,
            searchTerm: params.searchTerm || undefined,
            nroProveedor: params.nroProveedor || undefined,
            asentado: params.asentado || undefined,
            usuario: params.usuario || undefined,
            letra: params.letra || undefined,
            nroSuc: params.nroSuc ?? undefined,
            nroComprobante: params.nroComprobante ?? undefined
        };
        if (params.fechaDesde) {
            queryParams.fechaDesde = typeof params.fechaDesde === 'string'
                ? params.fechaDesde
                : params.fechaDesde.toISOString().split('T')[0];
        }
        if (params.fechaHasta) {
            queryParams.fechaHasta = typeof params.fechaHasta === 'string'
                ? params.fechaHasta
                : params.fechaHasta.toISOString().split('T')[0];
        }
        const response = await api.get<FacturaCompraPagedResult>('/api/FacturasCompra', {
            params: queryParams
        });
        return response.data;
    },

    getFacturaCompra: async (id: number): Promise<FacturaCompra> => {
        const response = await api.get<FacturaCompra>(`/api/FacturasCompra/${id}`);
        return response.data;
    },

    createFacturaCompra: async (payload: FacturaCompraCreatePayload): Promise<FacturaCompra> => {
        const response = await api.post<FacturaCompra>('/api/FacturasCompra', payload);
        return response.data;
    },

    getLibroIvaCompras: async (params: LibroIvaFacturaCompraSearchParams): Promise<LibroIvaComprasPagedResult> => {
        const queryParams: Record<string, string | number | undefined> = {
            PageNumber: params.pageNumber,
            PageSize: params.pageSize,
            SearchTerm: params.searchTerm || undefined
        };
        if (params.fechaDesde) {
            queryParams.FechaDesde = params.fechaDesde.toISOString().split('T')[0];
        }
        if (params.fechaHasta) {
            queryParams.FechaHasta = params.fechaHasta.toISOString().split('T')[0];
        }
        if (params.nroProveedor) {
            queryParams.NroProveedor = params.nroProveedor;
        }
        if (params.nroSuc) {
            queryParams.NroSuc = params.nroSuc;
        }
        if (params.nombreProveedor) {
            queryParams.NombreProveedor = params.nombreProveedor;
        }
        const response = await api.get<LibroIvaComprasPagedResult>('/api/ComprobantesCompra/libro-iva-compras', {
            params: queryParams
        });
        return response.data;
    }
};

export default facturaCompraService;

